import React, { useEffect, useState } from 'react';
import { Card, Button, Table, Modal, Form, Row, Col } from 'react-bootstrap';
import { useConfig } from "../utils/ConfigContext.jsx";
import { createAxiosInstance } from "../utils/AxiosInstance.jsx";

// --- Role-Module Assignment Modal ---
const AssignModulesModal = ({ show, handleClose, role, modules, onSave }) => {
    const [selectedModuleIds, setSelectedModuleIds] = useState(role?.moduleIds || []);

    useEffect(() => {
        setSelectedModuleIds(role?.moduleIds || []);
    }, [role]);

    const handleCheckboxChange = (moduleId) => {
        setSelectedModuleIds(prevIds =>
            prevIds.includes(moduleId)
                ? prevIds.filter(id => id !== moduleId)
                : [...prevIds, moduleId]
        );
    };

    const handleSave = () => {
        onSave(role.id, selectedModuleIds);
        handleClose();
    };

    return (
        <Modal show={show} onHide={handleClose}>
            <Modal.Header closeButton className="bg-info text-white">
                <Modal.Title>Assign Modules to: {role?.name}</Modal.Title>
            </Modal.Header>
            <Modal.Body>
                <Form>
                    <div style={{ maxHeight: '300px', overflowY: 'auto', paddingRight: '10px' }}>
                        {modules.map((module) => (
                            <Form.Check
                                key={module.id}
                                type="checkbox"
                                id={`module-check-${module.id}`}
                                label={`${module.name} (${module.link})`}
                                checked={selectedModuleIds.includes(module.id)}
                                onChange={() => handleCheckboxChange(module.id)}
                                disabled={module.status === 'Inactive'}
                                className="mb-2"
                            />
                        ))}
                    </div>
                </Form>
            </Modal.Body>
            <Modal.Footer>
                <Button variant="secondary" onClick={handleClose}>Close</Button>
                <Button variant="info" onClick={handleSave}>Save Assignments</Button>
            </Modal.Footer>
        </Modal>
    );
};

// --- Roles Card ---
const RolesCard = ({ modules, AxiosInstance }) => {
    const [roles, setRoles] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showEditModal, setShowEditModal] = useState(false);
    const [isEditing, setIsEditing] = useState(false);
    const [currentRole, setCurrentRole] = useState({});
    const [showAssignModal, setShowAssignModal] = useState(false);
    const [roleToAssign, setRoleToAssign] = useState({});

    // Fetch roles
    const getRoles = async () => {
        try {
            const res = await AxiosInstance.get('/roles');
           
            setRoles(res.data.data || []);
        } catch (error) {
            console.error("Error fetching roles:", error);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        getRoles();
    }, []);

    const handleEditShow = (role = {}) => {
        setIsEditing(!!role.id);
        setCurrentRole(role);
        setShowEditModal(true);
    };
    const handleEditClose = () => setShowEditModal(false);

    const handleAssignShow = (role) => {
        setRoleToAssign(role);
        setShowAssignModal(true);
    };
    const handleAssignClose = () => setShowAssignModal(false);

    const handleSaveAssignments = async (roleId, newModuleIds) => {
        try {
            await AxiosInstance.put(`/roles/${roleId}/modules`, { module_ids: newModuleIds });
            setRoles(prev =>
                prev.map(r => r.id === roleId ? { ...r, moduleIds: newModuleIds } : r)
            );
        } catch (error) {
            console.error("Error saving assignments:", error);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            if (isEditing) {
                const res = await AxiosInstance.put(`/roles/${currentRole.id}`, currentRole);
                setRoles(prev => prev.map(r => (r.id === res.data.id ? res.data : r)));
            } else {
                const res = await AxiosInstance.post('/roles', currentRole);
                setRoles(prev => [...prev, res.data]);
            }
            setShowEditModal(false);
        } catch (error) {
            console.error("Error saving role:", error);
        }
    };

    const handleDelete = async (id) => {
        if (!window.confirm('Are you sure you want to delete this role?')) return;
        try {
            await AxiosInstance.delete(`/roles/${id}`);
            setRoles(roles.filter(r => r.id !== id));
        } catch (error) {
            console.error("Error deleting role:", error);
        }
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        setCurrentRole(prev => ({ ...prev, [name]: value }));
    };

    if (loading) return <p>Loading roles...</p>;

    return (
        <Card className="shadow-sm">
            <Card.Header className="d-flex justify-content-between align-items-center  text-white">
                <h5 className="mb-0">Role List</h5>
                <Button variant="light bg-primary text-white" onClick={() => handleEditShow()}>
                    <i className="ri-add-circle-line me-2"></i> Add New Role
                </Button>
            </Card.Header>
            <Card.Body>
                <Table striped bordered hover responsive size="sm">
                    <thead>
                        <tr>
                            <th>Name</th>
                            <th>Description</th>
                            <th>Type</th>
                            <th>Status</th>
                            <th>Action</th>
                        </tr>
                    </thead>
                    <tbody>
                        {roles.map((role) => (
                            <tr key={role.id}>
                                <td>{role.name}</td>
                                <td>{role.description}</td>
                                <td>{role.type.toUpperCase()}</td>
                                <td>
                                    <span className={`badge ${role.status === 1 ? 'bg-success' : 'bg-danger'}`}>
                                       {role.status === 1 ? 'Active' : 'Inactive'}
                                    </span>
                                </td>
                                <td>
                                    <Button variant="outline-info" size="sm" className="me-2" onClick={() => handleAssignShow(role)}>
                                        <i className="ri-lock-2-line"></i> Assign
                                    </Button>
                                    <Button variant="outline-primary" size="sm" className="me-2" onClick={() => handleEditShow(role)}>
                                        <i className="ri-edit-line"></i>
                                    </Button>
                                    <Button variant="outline-danger" size="sm" onClick={() => handleDelete(role.id)}>
                                        <i className="ri-delete-bin-line"></i>
                                    </Button>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </Table>

                {/* Role Add/Edit Modal */}
                <Modal show={showEditModal} onHide={handleEditClose}>
                    <Modal.Header closeButton className="bg-primary text-white">
                        <Modal.Title>{isEditing ? 'Edit Role' : 'Add New Role'}</Modal.Title>
                    </Modal.Header>
                    <Form onSubmit={handleSubmit}>
                        <Modal.Body>
                            <Form.Group className="mb-3">
                                <Form.Label>Role Name</Form.Label>
                                <Form.Control
                                    type="text"
                                    name="name"
                                    value={currentRole.name || ''}
                                    onChange={handleChange}
                                    required
                                />
                            </Form.Group>
                            <Form.Group className="mb-3">
                                <Form.Label>Description</Form.Label>
                                <Form.Control
                                    as="textarea"
                                    rows={3}
                                    name="description"
                                    value={currentRole.description || ''}
                                    onChange={handleChange}
                                />
                            </Form.Group>
                            <Row>
                                <Col>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Type</Form.Label>
                                        <Form.Select name="type" value={currentRole.type || ''} onChange={handleChange} required>
                                            <option value="">Select Type</option>
                                            <option value="System">System</option>
                                            <option value="User Defined">User Defined</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                                <Col>
                                    <Form.Group className="mb-3">
                                        <Form.Label>Status</Form.Label>
                                        <Form.Select name="status" value={currentRole.status || ''} onChange={handleChange} required>
                                            <option value="Active">Active</option>
                                            <option value="Inactive">Inactive</option>
                                        </Form.Select>
                                    </Form.Group>
                                </Col>
                            </Row>
                        </Modal.Body>
                        <Modal.Footer>
                            <Button variant="secondary" onClick={handleEditClose}>Close</Button>
                            <Button variant="primary" type="submit">{isEditing ? 'Save Changes' : 'Create Role'}</Button>
                        </Modal.Footer>
                    </Form>
                </Modal>

                {/* Assign Modules Modal */}
                <AssignModulesModal
                    show={showAssignModal}
                    handleClose={handleAssignClose}
                    role={roleToAssign}
                    modules={modules}
                    onSave={handleSaveAssignments}
                />
            </Card.Body>
        </Card>
    );
};



// --- Main Page ---
const Roles = () => {
    const { API_BASE_URL } = useConfig();
    const AxiosInstance = createAxiosInstance(API_BASE_URL);

    return (
        <>
            <style>{`
                .modal-backdrop {
                    height: 100% !important;
                    width: 100% !important;
                }
            `}</style>

            <div className="py-4">
                <Row className="g-4">
                    <Col lg={12}>
                        <RolesCard modules={[]} AxiosInstance={AxiosInstance} />
                    </Col>
                    
                </Row>
            </div>
        </>
    );
};

export default Roles;
